/**
 * 答题记录管理页面
 */

import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams, Link } from 'react-router-dom';
import { formatInDisplayTz } from '@/lib/time';
import {
  ChartBarIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  FunnelIcon,
  DocumentDuplicateIcon,
  UserIcon,
} from '@heroicons/react/24/outline';
import { attemptApi, quizApi, userApi } from '@/lib/api';
import type { Attempt } from '@/types';

export default function AttemptManagement() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 20;

  const quizId = searchParams.get('quiz_id') || '';
  const userId = searchParams.get('user_id') || '';

  // 获取答题记录
  const { data: attemptsData, isLoading } = useQuery({
    queryKey: ['attempts', quizId, userId, currentPage],
    queryFn: () => attemptApi.getAttempts({
      quiz_id: quizId || undefined,
      user_id: userId || undefined,
      page: currentPage,
      limit: pageSize,
    }),
  });

  // 获取题集列表用于筛选
  const { data: quizzesData } = useQuery({
    queryKey: ['quizzes'],
    queryFn: () => quizApi.getQuizzes(),
  });

  const attempts: Attempt[] = attemptsData?.attempts || [];
  const pagination = attemptsData?.pagination;
  const quizzes = quizzesData?.quizzes || [];

  const updateFilters = (key: string, value: string) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set(key, value);
    } else {
      newParams.delete(key);
    }
    setSearchParams(newParams);
    setCurrentPage(1);
  };

  const getStatusIcon = (attempt: any) => {
    if (!attempt.completed_at) {
      return <ClockIcon className="h-5 w-5 text-yellow-500" title="未完成" />;
    } else if (attempt.is_all_correct) {
      return <CheckCircleIcon className="h-5 w-5 text-green-500" title="全部正确" />;
    } else {
      return <XCircleIcon className="h-5 w-5 text-red-500" title="有错误" />;
    }
  };

  const formatDuration = (durationMs: number | null | undefined) => {
    if (!durationMs) return '-';
    const minutes = Math.floor(durationMs / 60000);
    const seconds = Math.floor((durationMs % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">答题记录</h1>
        <p className="mt-2 text-sm text-gray-600">
          查看所有用户的答题记录和成绩
        </p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center gap-4">
          <FunnelIcon className="h-5 w-5 text-gray-400" />
          <div className="flex gap-4 flex-1">
            <div className="flex-1">
              <label className="form-label text-sm">题集筛选</label>
              <select
                value={quizId}
                onChange={(e) => updateFilters('quiz_id', e.target.value)}
                className="form-select text-sm"
              >
                <option value="">所有题集</option>
                {quizzes.map(quiz => (
                  <option key={quiz.id} value={quiz.id}>
                    {quiz.title}
                  </option>
                ))}
              </select>
            </div>
            {userId && (
              <div className="flex-1">
                <label className="form-label text-sm">当前用户</label>
                <div className="text-sm text-gray-600 py-2">
                  用户ID: {userId.slice(0, 12)}...
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Attempts List */}
      <div className="bg-white rounded-lg shadow">
        {isLoading ? (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : attempts.length === 0 ? (
          <div className="text-center py-12">
            <ChartBarIcon className="h-12 w-12 mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">没有答题记录</h3>
            <p className="text-gray-600">
              {quizId || userId ? '当前筛选条件下没有找到答题记录' : '还没有用户开始答题'}
            </p>
          </div>
        ) : (
          <>
            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      状态
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      用户
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      题集
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      成绩
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      耗时
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      开始时间
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      完成时间
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {attempts.map((attempt: Attempt) => (
                    <tr key={attempt.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          {getStatusIcon(attempt)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <UserIcon className="h-4 w-4 mr-2 text-gray-400" />
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {attempt.nickname || '匿名用户'}
                            </div>
                            <div className="text-xs text-gray-500">
                              {attempt.user_id?.slice(0, 8)}...
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <DocumentDuplicateIcon className="h-4 w-4 mr-2 text-gray-400" />
                          <div className="text-sm text-gray-900">
                            {attempt.quiz_title || attempt.quiz_id}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {attempt.completed_at ? (
                          <div className="text-sm">
                            <span className={`font-medium ${
                              attempt.is_all_correct ? 'text-green-600' : 'text-red-600'
                            }`}>
                              {attempt.score || 0}/{attempt.total || 0}
                            </span>
                            <div className="text-xs text-gray-500">
                              {Math.round(((attempt.score || 0) / (attempt.total || 1)) * 100)}%
                            </div>
                          </div>
                        ) : (
                          <span className="text-sm text-yellow-600">未完成</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {formatDuration(attempt.duration_ms)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {formatInDisplayTz(attempt.started_at, 'yyyy-MM-dd HH:mm')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {attempt.completed_at 
                          ? formatInDisplayTz(attempt.completed_at, 'yyyy-MM-dd HH:mm')
                          : '-'
                        }
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination && pagination.totalPages > 1 && (
              <div className="px-6 py-4 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-600">
                    显示 {((currentPage - 1) * pageSize) + 1} - {Math.min(currentPage * pageSize, pagination.total)} 条，共 {pagination.total} 条
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setCurrentPage(currentPage - 1)}
                      disabled={currentPage <= 1}
                      className="btn btn-secondary text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      上一页
                    </button>
                    <span className="flex items-center px-3 py-1 text-sm">
                      {currentPage} / {pagination.totalPages}
                    </span>
                    <button
                      onClick={() => setCurrentPage(currentPage + 1)}
                      disabled={currentPage >= pagination.totalPages}
                      className="btn btn-secondary text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      下一页
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}